import { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Drawer } from '@JohnLewisPartnership/jl-design-system/components/drawer/Drawer';
import { PrimaryButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import styles from './FilterDrawer.module.scss';
import ClearFilters from '../facets/clear-filters/ClearFilters';
import Facets from '../facets/Facets';
import Loading from '../loading/Loading';
import { useFilterDrawerData } from './data/useFilterDrawerData';

const FilterDrawer = (props) => {
  const {
    closeFilterDrawer,
    isMoreFiltersButton = false,
    isPrimaryFacetMainFacet = false,
    onAfterOpen,
    openFilterDrawer = false,
    primaryFacetClassName,
    totalNumberOfResults,
    position = 'left',
  } = props;

  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [urlHasChanged, setUrlHasChanged] = useState(false);
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [displayFacets, setDisplayFacets] = useState(false);

  const { experiencesContext, facetContext, router } = useFilterDrawerData();

  const handleFilterDrawerClose = () => {
    setTimeout(() => {
      setDisplayFacets(false);
    }, 400);

    if (closeFilterDrawer) closeFilterDrawer();

    const testId = isMoreFiltersButton
      ? 'more-filters-button'
      : `filters-button${
          primaryFacetClassName ? `-${primaryFacetClassName}` : ''
        }`;

    // safari fix - see https://github.com/reactjs/react-modal/issues/858
    if (!document.activeElement.matches(`[data-testid="${testId}"]`)) {
      const filterButton = document.querySelector(`[data-testid="${testId}"]`);
      if (filterButton) {
        filterButton.focus();
      }
    }
    if (urlHasChanged) {
      window.scrollTo(0, 0);
      setUrlHasChanged(false);
    }
  };

  const handleRouteChangeStart = (url, { shallow }) => {
    if (!shallow) {
      setIsLoadingPage(true);
    }
  };
  const handleRouteChangeComplete = (url, { shallow }) => {
    if (!shallow) {
      setIsLoadingPage(false);
    }
    setUrlHasChanged(true);
  };

  useEffect(() => {
    const { events } = router;
    events.on('routeChangeStart', handleRouteChangeStart);
    events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      events.off('routeChangeComplete', handleRouteChangeComplete);
      events.off('routerChangeComplete', handleRouteChangeComplete);
    };
  });

  useEffect(() => {
    if (openFilterDrawer && !drawerIsOpen) {
      setDrawerIsOpen(true);
      setDisplayFacets(true);
    }

    if (!openFilterDrawer && drawerIsOpen) {
      setDrawerIsOpen(false);
    }
  }, [drawerIsOpen, onAfterOpen, openFilterDrawer]);

  useEffect(() => {
    if (drawerIsOpen && displayFacets) {
      if (onAfterOpen) onAfterOpen();
    }
  }, [drawerIsOpen, displayFacets]);

  const { isTabletEnhancementsFlagOn } = experiencesContext;

  const { baseFacetId, applied } = facetContext;

  const hasLargeButton =
    typeof window !== 'undefined' && window.innerWidth > 539;

  const hasAppliedFilters = applied?.filterCombination?.length > 0;
  const isPositionRight = position === 'right';

  const filterFooterActionButtons = () => (
    <div
      className={classNames(styles.footerStyleModifier, {
        [styles['footerStyleModifier--right']]: isPositionRight,
      })}
    >
      {((isPositionRight && hasAppliedFilters) || !isPositionRight) && (
        <ClearFilters
          baseFacetId={baseFacetId}
          hasLargeButton={hasLargeButton}
          mobileFilterDrawer
          query={{ page: undefined }}
          show
        />
      )}

      <PrimaryButton
        className={styles.actionButton}
        data-testid="filters-result-button"
        onClick={closeFilterDrawer}
        size="small"
      >
        View {totalNumberOfResults} result{totalNumberOfResults !== 1 && 's'}
      </PrimaryButton>
    </div>
  );

  return (
    <Fragment>
      <Drawer
        className={classNames(styles.filterDrawer, {
          [styles['tabletEnhancements']]: isTabletEnhancementsFlagOn,
          [styles['position--right']]: position === 'right',
        })}
        heading="Filters"
        position={position}
        isOpen={openFilterDrawer}
        onClose={handleFilterDrawerClose}
      >
        {isLoadingPage && <Loading showSpinner={false} />}
        {!displayFacets && <Loading showSpinner={true} />}
        {displayFacets && (
          <Facets
            isFilterDrawer
            isMobile
            isPrimaryFacetMainFacet={isPrimaryFacetMainFacet}
            openFacetName={primaryFacetClassName}
          />
        )}
        {filterFooterActionButtons()}
      </Drawer>
    </Fragment>
  );
};

FilterDrawer.propTypes = {
  closeFilterDrawer: PropTypes.func,
  isMoreFiltersButton: PropTypes.bool,
  isPrimaryFacetMainFacet: PropTypes.bool,
  onAfterOpen: PropTypes.func,
  openFilterDrawer: PropTypes.bool,
  primaryFacetClassName: PropTypes.string,
  totalNumberOfResults: PropTypes.number,
};

export default FilterDrawer;
